import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output } from '@angular/core';

import { ChassisFormHandler } from '../../classes/form-handler';
import { AuthenticationService } from '../../../../authentication/services/authentication.service';
import { RouteService } from '../../../../routing/services/route.service';
import { UserModel } from '../../../../authentication/models/user.model';
import { LoaderService } from '../../../../support/loading/loader.service';
import { Subject } from 'rxjs/Subject';

@Component({
    selector: 'chassis-form',
    templateUrl: './form.component.html',
    styleUrls: ['./form.component.scss']
})
export class FormComponent implements OnInit, OnDestroy {

    /**
     * The ChassisFormHandler form instance.
     */
    @Input() form: ChassisFormHandler;

    /**
     * Subject that contains the model that is used
     */
    @Input() modelSubject: Subject<any>;

    /**
     * The form classes.
     */
    @Input() class: string;

    /**
     * Form layout.
     */
    @Input() layout: string;

    /**
     * The onSubmit event.
     *
     * @type {EventEmitter}
     */
    @Output() onSubmit = new EventEmitter();

    /**
     * The onSuccess event.
     *
     * @type {EventEmitter}
     */
    @Output() onSuccess = new EventEmitter();

    /**
     * The onError event.
     *
     * @type {EventEmitter}
     */
    @Output() onError = new EventEmitter();

    /**
     * The onLoad event.
     *
     * @type {EventEmitter}
     */
    @Output() onLoad = new EventEmitter();

    /**
     * The Loader instance.
     */
    public loader;

    /**
     * The AuthenticationService isntance.
     *
     * @type {AuthenticationService}
     */
    public authenticationService: AuthenticationService;

    /**
     * The LoaderService instance.
     *
     * @type {LoaderService}
     */
    public loaderService: LoaderService;

    /**
     * The RouteService isntance.
     *
     * @type {RouteService}
     */
    public routeService: RouteService;

    /**
     * Constructor.
     *
     * @param injector
     */
    constructor(protected injector: Injector) {
        this.loaderService         = injector.get(LoaderService);
        this.routeService          = this.injector.get(RouteService);
        this.authenticationService = this.injector.get(AuthenticationService);

        this.loader = this.loaderService.create('form');
    }

    /**
     * OnInit.
     */
    ngOnInit() {
        if (!this.form) {
            throw Error('No form handler specified.');
        } else {
            if (this.form.entityName) {
                this.filterEmptyFormGroups();
            }
        }

        this.setFormLayout();
    }

    /**
     * Set the form layout.
     */
    protected setFormLayout() {
        this.layout = this.layout ? this.layout : !this.class || this.class.includes('form-horizontal') ? 'horizontal' : 'inline';
    }

    /**
     * Check if the form layout is of the given type.
     *
     * @param name
     * @returns {boolean}
     */
    protected isLayout(name: string) {
        return this.layout === name;
    }

    /**
     * Form submit.
     */
    public submit() {
        this.form.submit();

        this.startLoading();

        this.form.observer.subscribe((data) => {
            this.onSubmit.emit(data);
        }, (error) => {
            this.onError.emit(error);
        }, () => {
            this.stopLoading();
        });
    }

    public groupHasErrors(group) {
        const fields = this.getFieldsInGroup(group);

        const groupErrors = fields.filter(field => {
            return (this.form.formGroup.controls[field] && this.form.formGroup
                    .controls[field].errors && this.form.formGroup.controls[field].dirty)
                || this.form.backendErrors[field];
        });

        return groupErrors.length;
    }

    protected getFieldsInGroup(group) {
        let fields = [];

        group.sections.forEach(section => {
            section.rows.forEach(row => {
                fields = fields.concat(row.elements.map(element => element.name));
            });
        });

        return fields;
    }

    /**
     * Start form loader.
     */
    protected startLoading() {
        this.loader.start();

        this.onLoad.emit(this.loader.isLoading);
    }

    /**
     * Stop form loader.
     */
    protected stopLoading() {
        this.loader.stop();

        this.onLoad.emit(this.loader.isLoading);
    }

    private filterEmptyFormGroups() {
        this.authenticationService.user().subscribe((user: UserModel) => {
            let elementsVisible;

            for (const group of this.form.structure.groups) {
                elementsVisible = 0;

                for (const section of group.sections) {
                    for (const row of section.rows) {
                        for (const element of row.elements) {
                            if (user.can('entities.' + this.form.entityName + '.' + element.name + '.read')) {
                                elementsVisible++;
                            }
                        }
                    }
                }

                if (!elementsVisible) {
                    this.form.structure.groups = this.form.structure.groups
                        .filter((formStructureGroup) => formStructureGroup !== group);
                }
            }
        });
    }

    ngOnDestroy() {
        this.loaderService.destroy('form');
    }

}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/forms/form/components/form/form.component.ts